/**
 * Settings, one place for everything that isn't a case (Oct 2026 revamp).
 *
 * Borrowed from Granted's portal: a list on the left, the chosen page on
 * the right. Account, Plan, Family and Contact keep their own URLs (emails
 * and SMS link straight to /plan and /account) and just render inside the
 * right-hand pane now. On a phone /settings is the list on its own, and
 * each page gets a "Settings" back link instead.
 */

import { useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  CreditCard,
  ExternalLink,
  LifeBuoy,
  LogOut,
  MessageSquareWarning,
  Settings as SettingsIcon,
  User,
  Users,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { showIntercom } from '../lib/intercom';
import ConfirmDialog from './ConfirmDialog';
import '@styles/components/settings.css';

type Row = {
  key: string;
  label: string;
  hint?: string;
  Icon: typeof User;
  to?: string;
  href?: string;
  onClick?: () => void;
};

export default function SettingsLayout({ children }: { children?: ReactNode }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [confirmLogout, setConfirmLogout] = useState(false);

  const isIndex = pathname === '/settings';

  const yours: Row[] = [
    { key: 'account', label: 'Account', hint: user?.email ?? undefined, Icon: User, to: '/account' },
    { key: 'family', label: 'Family', hint: 'Spouse and children', Icon: Users, to: '/family-members' },
    { key: 'plan', label: 'Plan & billing', Icon: CreditCard, to: '/plan' },
  ];
  const help: Row[] = [
    { key: 'contact', label: 'Contact us', Icon: LifeBuoy, to: '/contact' },
    {
      key: 'bug',
      label: 'Report a problem',
      Icon: MessageSquareWarning,
      onClick: () => showIntercom('Something in the app isn\'t working: '),
    },
  ];
  const legal: Row[] = [
    { key: 'privacy', label: 'Privacy Policy', Icon: ExternalLink, href: 'https://mediloop.ai/privacy-policy' },
    { key: 'terms', label: 'Terms of Service', Icon: ExternalLink, href: 'https://mediloop.ai/terms-of-service' },
  ];

  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.href = 'https://mediloop.ai/';
  };

  const renderRow = (r: Row, quiet = false) => {
    const active = r.to === pathname;
    const inner = (
      <>
        {!quiet && <r.Icon size={17} strokeWidth={1.75} className="st-row-icon" />}
        <span className="st-row-text">
          <span className="st-row-label">{r.label}</span>
          {r.hint && <span className="st-row-hint">{r.hint}</span>}
        </span>
        {r.href ? (
          <ExternalLink size={14} className="st-row-chev" aria-hidden />
        ) : (
          <ChevronRight size={16} className="st-row-chev" aria-hidden />
        )}
      </>
    );
    const cls = `st-row ${quiet ? 'st-row--quiet' : ''} ${active ? 'active' : ''}`;
    if (r.href) {
      return (
        <a key={r.key} className={cls} href={r.href} target="_blank" rel="noreferrer">
          {inner}
        </a>
      );
    }
    return (
      <button
        key={r.key}
        type="button"
        className={cls}
        aria-current={active ? 'page' : undefined}
        onClick={() => (r.to ? navigate(r.to) : r.onClick?.())}
      >
        {inner}
      </button>
    );
  };

  return (
    <div className={`st ${isIndex ? 'st--index' : 'st--page'}`}>
      <header className="st-head">
        <h1 className="ml-title st-title">Settings</h1>
      </header>

      <div className="st-body">
        <nav className="st-list" aria-label="Settings">
          <div className="st-group">{yours.map((r) => renderRow(r))}</div>
          <div className="st-group">{help.map((r) => renderRow(r))}</div>
          <div className="st-group st-group--quiet">
            {legal.map((r) => renderRow(r, true))}
            <button type="button" className="st-row st-row--quiet" onClick={() => setConfirmLogout(true)}>
              <span className="st-row-text">
                <span className="st-row-label">Log out</span>
              </span>
              <LogOut size={15} className="st-row-chev" aria-hidden />
            </button>
          </div>
        </nav>

        <section className="st-pane">
          {isIndex ? (
            <div className="ml-empty st-placeholder">
              <span className="ml-empty-icon"><SettingsIcon size={28} strokeWidth={1.5} /></span>
              <p className="ml-empty-title">Settings</p>
              <p className="ml-empty-body">Pick something on the left.</p>
            </div>
          ) : (
            <>
              <button type="button" className="st-back" onClick={() => navigate('/settings')}>
                <ChevronLeft size={16} /> Settings
              </button>
              {children}
            </>
          )}
        </section>
      </div>

      <ConfirmDialog
        open={confirmLogout}
        title="Log out?"
        message="You'll need to sign in again to see your cases."
        confirmLabel="Log out"
        cancelLabel="Cancel"
        onConfirm={handleLogout}
        onCancel={() => setConfirmLogout(false)}
        variant="danger"
      />
    </div>
  );
}
